/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, Input, OnInit, inject} from '@angular/core';

import {GeneralDataHandlingService} from '../../../../../services/general.data-handling.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {PieComponent} from '../charts/pie/pie.component';
import {BarComponent} from '../charts/bar/bar.component';
import {FormFieldAttribute} from '../../../../../models/FormCategory';
import {LineComponent} from '../charts/line/line.component';
import {DocumentComponent} from '../document/document.component';
import {DetailComponent} from '../detail/detail.component';
import {LanguageComponent} from '../language/language.component';
import {AddressComponent} from '../address/address.component';
import {IxModule} from '@siemens/ix-angular';
import {KeyValueComponent} from '../key-value/key-value.component';
import {PassportDto} from '../../../../../models/Passport';
import {PassportTranslationService} from '../../../../../services/passport-translation.service';
import {
  LanguageEntry,
  LanguagePickerComponent,
  languageCodeFor,
} from '../language-picker/language-picker.component';
import {isLanguageColumn} from '../../utils/language-column.util';
import {isVisibleIfSatisfied} from '../../../utils/visible-if';
import {formatDateTimeValue} from '../../../utils/date-time-format';
import {
  getDocumentUrlPath,
  isDocumentObjectAttribute,
  toDocumentObject,
} from '../../../utils/document-object';

@Component({
  selector: 'app-viewer-field',
  imports: [
    TranslateModule,
    PieComponent,
    BarComponent,
    LineComponent,
    DocumentComponent,
    DetailComponent,
    IxModule,
    LanguageComponent,
    AddressComponent,
    KeyValueComponent,
    LanguagePickerComponent,
  ],
  templateUrl: './viewer-field.component.html',
  styleUrl: './viewer-field.component.scss',
})
export class ViewerFieldComponent implements OnInit {
  private readonly dataHandler = inject(GeneralDataHandlingService);
  private readonly translate = inject(TranslateService);
  private readonly passportTranslation = inject(PassportTranslationService);

  @Input({required: true}) attribute!: FormFieldAttribute;
  @Input({required: true}) passport!: PassportDto['data'];
  @Input({required: true}) schemaId!: string;
  /** JSON-pointer-style prefix prepended to `attribute.valuePath` to identify this exact instance. */
  @Input() valuePathPrefix = '';

  /** Whether a sub-attribute is the language column of a `list:language` entry (not rendered as content). */
  protected readonly isLanguageColumn = isLanguageColumn;

  value!: any;
  preCheck?: boolean;
  fullValuePath!: string;

  ngOnInit() {
    this.value = this.dataHandler.getValueByPath(
      this.passport,
      this.attribute.valuePath,
    );
    this.fullValuePath = this.valuePathPrefix + this.attribute.valuePath;
    this.preCheckValue();
  }

  preCheckValue(): void {
    if (!isVisibleIfSatisfied(this.attribute.visibleIf, this.passport)) {
      this.preCheck = false;
      return;
    }
    if (isDocumentObjectAttribute(this.attribute)) {
      const urlPath = getDocumentUrlPath(this.attribute);
      this.preCheck = Boolean(
        this.dataHandler.getValueByPath(
          toDocumentObject(this.value, urlPath),
          urlPath,
        ),
      );
      return;
    }
    switch (this.attribute.type.split(':').at(0)) {
      case 'address':
        this.preCheck = this.checkAddresses(this.attribute, this.passport);
        break;
      case 'chart':
      case 'list':
        this.preCheck =
          Array.isArray(this.value) &&
          this.dataHandler.atLeastOneFieldSetIn(this.value);
        break;
      default:
        this.preCheck = Boolean(this.value);
    }
  }

  checkAddresses(attribute: FormFieldAttribute, passport: unknown): boolean {
    let preCheckAddress = false;
    for (const subAttribute of attribute.attributes || []) {
      const value =
        this.dataHandler.getValueByPath(
          passport,
          `${attribute.valuePath}.${subAttribute.valuePath}`,
        ) || '';
      if (value !== null && value !== undefined && value !== '') {
        preCheckAddress = true;
      }
    }
    return preCheckAddress;
  }

  getUnit(): string {
    const translateValue = `general.units.${this.attribute.unit}`;
    const translated = this.translate.instant(translateValue);
    if (translated === translateValue) {
      return this.attribute.unit || '';
    } else {
      return translated;
    }
  }

  getLabel(): string {
    return this.passportTranslation.translateByPath(
      this.attribute.valuePath,
      this.schemaId,
      this.attribute.translation.label,
    );
  }

  parseDate(): string {
    const value: string =
      this.dataHandler.getValueByPath(
        this.passport,
        this.attribute.valuePath,
      ) || '';
    return formatDateTimeValue(
      value,
      this.attribute.format,
      this.attribute.timeZone,
      this.translate.currentLang || this.translate.defaultLang,
    );
  }

  parseSelect(): string {
    const value = this.dataHandler.getValueByPath(
      this.passport,
      this.attribute.valuePath,
    );
    if (!value) {
      return '';
    }
    const translated = this.passportTranslation.translateValueByPath(
      this.attribute.valuePath,
      this.schemaId,
      value,
    );
    if (translated !== value) {
      return translated;
    }
    if (this.attribute.options) {
      const option = this.attribute.options.find((opt) => opt.value === value);
      return option ? option.label : value;
    }
    return value;
  }

  getNameId(): {name: string; id: string} {
    return {
      name: this.dataHandler.getValueByPath(
        this.value,
        this.attribute.attributes![0].valuePath,
      ),
      id: this.dataHandler.getValueByPath(
        this.value,
        this.attribute.attributes![1].valuePath,
      ),
    };
  }

  /**
   * Finds the `list:language` entry matching the given language code, used
   * to render only the Effective Language's content for that instance.
   *
   * @param entries - The instance's own language entries (e.g. `documentLanguages`).
   * @param languageCode - The Effective Language resolved by `app-language-picker`.
   */
  findEntryByLanguage(
    entries: LanguageEntry[],
    languageCode: string | undefined,
  ): LanguageEntry | undefined {
    return languageCode
      ? entries?.find((entry) => languageCodeFor(entry) === languageCode)
      : undefined;
  }
}
